import { getValidAccessToken, refreshAccessToken } from '../auth/token';
import { getAuthState } from '../auth/store';

import { ApiError, ClientErrorCode, apiErrorFromResponse } from './errors';

/** All REST endpoints live under this prefix (same origin; nginx / Vite proxy to the API). */
export const API_BASE = '/api';

export type QueryPrimitive = string | number | boolean | Date;
export type QueryValue = QueryPrimitive | null | undefined | readonly QueryPrimitive[];
export type QueryParams = Record<string, QueryValue>;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  /** Appended as a query string; `null`/`undefined` values are skipped, arrays repeat the key. */
  query?: QueryParams;
  /** Serialized as JSON unless it is FormData, Blob, URLSearchParams or a string. */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Attach the bearer token and transparently refresh it on 401 (default true). */
  auth?: boolean;
  /** Sees the successful response before its body is read (e.g. to read response headers). */
  onResponse?: (response: Response) => void;
}

function serializeQueryValue(value: QueryPrimitive): string {
  return value instanceof Date ? value.toISOString() : String(value);
}

/** Builds `/api/<path>?<query>`; absolute paths starting with `/api` or `http` are kept as is. */
export function buildUrl(path: string, query?: QueryParams): string {
  const base =
    path.startsWith('http') || path.startsWith(API_BASE + '/')
      ? path
      : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value as readonly QueryPrimitive[]) params.append(key, serializeQueryValue(item));
    } else {
      params.append(key, serializeQueryValue(value as QueryPrimitive));
    }
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

function isRawBody(body: unknown): body is BodyInit {
  return (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer
  );
}

async function send(url: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    if (isRawBody(options.body)) {
      body = options.body;
    } else {
      body = JSON.stringify(options.body);
      headers['Content-Type'] = 'application/json';
    }
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    return await fetch(url, {
      method: options.method ?? (body === undefined ? 'GET' : 'POST'),
      headers,
      body,
      signal: options.signal,
      credentials: 'same-origin',
    });
  } catch (cause) {
    if (options.signal?.aborted) {
      throw new ApiError({ status: 0, code: ClientErrorCode.aborted, message: 'Request aborted', cause });
    }
    throw new ApiError({ status: 0, code: ClientErrorCode.network, message: 'Network error', cause });
  }
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') return undefined as T;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('json')) {
    try {
      return (await response.json()) as T;
    } catch (cause) {
      throw new ApiError({
        status: response.status,
        code: ClientErrorCode.invalidResponse,
        message: 'Invalid JSON response',
        cause,
      });
    }
  }
  if (contentType.startsWith('text/')) return (await response.text()) as T;
  return (await response.blob()) as T;
}

/**
 * Performs an API request and returns the parsed body.
 * Throws `ApiError` for non-2xx responses (ProblemDetails mapped to `code` + `fieldErrors`) and transport failures.
 * On 401 the access token is refreshed once (single-flight across concurrent requests) and the request retried.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query);
  const useAuth = options.auth ?? true;

  let token = useAuth ? await getValidAccessToken() : null;
  let response = await send(url, options, token);

  if (response.status === 401 && useAuth && getAuthState().status !== 'anonymous') {
    token = await refreshAccessToken();
    if (token) {
      response = await send(url, options, token);
      if (response.status === 401) getAuthState().clear('expired');
    }
  }

  if (!response.ok) throw await apiErrorFromResponse(response);
  options.onResponse?.(response);
  return parseBody<T>(response);
}

type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>;
type BodyOptions = Omit<RequestOptions, 'method'>;

/** Thin typed helpers over `apiRequest`. Paths are relative to `/api`, e.g. `api.get('/users', { query })`. */
export const api = {
  get: <T>(path: string, options?: BodylessOptions) => apiRequest<T>(path, { ...options, method: 'GET' }),
  delete: <T = void>(path: string, options?: BodyOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
  post: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  patch: <T = void>(path: string, body?: unknown, options?: BodylessOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
};
